/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import {
  Component,
  ComponentFactoryResolver,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { AnalyticsService } from "./@core/utils/analytics.service";
import { SeoService } from "./@core/utils/seo.service";
import { NbDialogService } from "@nebular/theme";
import { PopupBlockedDialogComponent } from "./shared/dialogs/popup-blocked-dialog/popup-blocked-dialog.component";
import { PopupBlockerService } from "./shared/dialogs/popup-blocked-dialog/service/popup-blocked-dialog.service";
import { MsalService } from "@azure/msal-angular";
import { Router } from "@angular/router";
import { AuthenticationResult } from "@azure/msal-browser";
import { SessionService } from "./@core/session/session.service";
import { IDGenerator } from "./@core/utils/id-generator.util";
import { AppLocalStorage } from "./@core/utils/local-storage.util";
import { TokenService } from "./@core/token/token.service";
import { UserTrackingService } from "./@core/utils";
import { TeamsService } from "./@core/utils/teams.service";

@Component({
  selector: "ngx-app",
  template: `
    <!-- This is the modal container where the dynamic component will be injected -->
    <ng-template #modalContainer></ng-template>
    <!-- Other components like router outlet -->
    <tui-root>
      <router-outlet></router-outlet>
    </tui-root>
  `,
})
export class AppComponent implements OnInit {
  constructor(
    private analytics: AnalyticsService,
    private seoService: SeoService,
    private dialogService: NbDialogService,
    private popupBlockerService: PopupBlockerService,
    private resolver: ComponentFactoryResolver,
    private msalService: MsalService,
    private router: Router,
    private sessionService: SessionService,
    private tokenService: TokenService,
    private userTrackService: UserTrackingService,
    private teamsService: TeamsService,
  ) {}

  // Keep track of list of generated components for removal purposes
  components = [];
  session: { token: any; id: any; expiry: any };

  // Expose class so that it can be used in the template
  componentClass = PopupBlockedDialogComponent;

  @ViewChild("modalContainer", { read: ViewContainerRef })
  modalContainer!: ViewContainerRef;

  async ngOnInit(): Promise<void> {
    // Initialize Teams SDK first
    // await this.initializeTeams();

    this.analytics.trackPageViews();
    this.seoService.trackCanonicalChanges();
    const checkSession = () => {
      const sessionData = AppLocalStorage.get("SESSION", "SESSION");
      if (sessionData?.token) {
        this.userTrackService.track(
          "OnAppCompnent_Load",
          { value: "Onload" },
          "App",
        );
      } else {
        setTimeout(checkSession, 500); // Retry after 500ms
      }
    };
    checkSession();

    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        if (result) {
          this.msalService.instance.setActiveAccount(result.account);
          let activeAccount = this.msalService.instance.getActiveAccount();

          this.session = {
            token: result.accessToken,
            id: IDGenerator.newId(),
            expiry: null,
          };
          AppLocalStorage.set("SESSION", "SESSION", this.session);

          this.sessionService.loadAccount().subscribe(
            () => console.log("Account loaded successfully (browser)"),
            (error) =>
              console.error("Failed to load account (browser):", error),
          );
          this.getCalendarEvents(result.account);

          // return;

          // Perform actions related to user accounts here
        }
      },
      error: (error) => console.log(error),
    });

    setInterval(async () => {
      if (this.teamsService.isRunningInTeams()) {
        // Check if Teams token is expiring soon and refresh it
        const expiry = sessionStorage.getItem("teams_sso_expiry");
        if (expiry && parseInt(expiry) * 1000 - Date.now() < 5 * 60 * 1000) {
          const refreshed = await this.teamsService.authenticateWithTeamsSSO();
          if (refreshed) {
            // Update SESSION in localStorage with new token
            const teamsToken = sessionStorage.getItem("teams_sso_token");
            if (teamsToken) {
              const payload = JSON.parse(atob(teamsToken.split(".")[1]));
              this.session = {
                token: teamsToken,
                id: IDGenerator.newId(),
                expiry: new Date(payload.exp * 1000),
              };
              AppLocalStorage.set("SESSION", "SESSION", this.session);
            }
          }
        }
      } else {
        // Browser: refresh MSAL token
        await this.tokenService.getAccessToken();
      }
    }, 30 * 1000);
  }

  getCalendarEvents(account) {
    // const account = this.authService.instance.getActiveAccount();
    if (account) {
      this.msalService
        .acquireTokenSilent({
          scopes: ["calendars.read"],
          account: account,
        })
        .subscribe({
          next: (response) => {
            AppLocalStorage.set(
              "CALENDAR_TOKEN",
              "CALENDAR_TOKEN",
              response.accessToken,
            );
            // this.http.get('https://graph.microsoft.com/v1.0/me/events', {
            //   headers: {
            //     Authorization: `Bearer ${response.accessToken}`
            //   }
            // }).subscribe(events => {
            // });
          },
          error: (error) => {
            // Handle the error or try to acquire a token interactively if needed
          },
        });
    }
  }

  openModal() {
    // Create component dynamically inside the ng-template
    const componentFactory = this.resolver.resolveComponentFactory(
      this.componentClass,
    );
    const component = this.modalContainer.createComponent(componentFactory);

    // Push the component so that we can keep track of which components are created
    this.components.push(component);
  }

  /**
   * Initialize Microsoft Teams SDK
   */
  async initializeTeams(): Promise<void> {
    try {
      const isTeamsInitialized = await this.teamsService.initialize();

      if (isTeamsInitialized) {
        const context = this.teamsService.getContext();

        // Authenticate using Teams SSO with MSAL integration
        const authenticated =
          await this.teamsService.authenticateWithTeamsSSO();
        if (authenticated) {

          // Get Teams token and store it
          const teamsToken = sessionStorage.getItem("teams_sso_token");
          if (teamsToken) {
            // Store in SESSION for app compatibility
            const payload = JSON.parse(atob(teamsToken.split(".")[1]));
            this.session = {
              token: teamsToken,
              id: IDGenerator.newId(),
              expiry: new Date(payload.exp * 1000),
            };
            AppLocalStorage.set("SESSION", "SESSION", this.session);

            // Load user account data
            this.sessionService.loadAccount().subscribe(
              () => console.log("Account loaded successfully (Teams)"),
              (error) =>
                console.error("Failed to load account (Teams):", error),
            );
          }
        } else {
          console.log("Teams SSO failed, user may need to login");
        }
      } else {
        console.log("Not running in Microsoft Teams");
      }
    } catch (error) {
      console.error("Error initializing Teams:", error);
    }
  }
  closeModal() {
    this.modalContainer.clear(); // Destroy the modal component when closed
  }
}
